import type { FC } from 'react';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpTab , DepositTab, SettingsModalFooter, LanguageList as LanguageListComponent, SettingTab } from './components';
import { Modal } from '../../shared/components/Modal';
import { externalCommunication } from '../../core/services/communication/ExternalCommunication';
import { useAppStore, useDemo, setSelectedSection, setViewMode } from '../../store/app/appStore';
import { useGameSounds } from '../../shared/hooks/useSound';
import { useSettleScrollOnRotate } from '../../shared/hooks/useSettleScrollOnRotate';
import { useEnableLobbyDeposit } from '../../store/featureFlags/featureFlagsStore';
import { clickCounter } from '../../core/services/EventTracker';
import { testIds } from '../../core/testIds';

import { TABS_META } from './tabsMeta';
import type { TabType } from './types';

import { LANGUAGE, SETTINGS, HELP, LOBBY, EXIT, DEPOSIT } from './constants';

const MODAL_WIDTH = 763;
const MODAL_HEIGHT = 530;

// Inner component that only renders when modal is open
const SettingsModalContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const languageCode = useAppStore((state) => state.settings.languageCode);
  const supportedLanguages = useAppStore((state) => state.supportedLanguages);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const assetsHost = useAppStore((state) => state.settings.assetsHost);
  const assetsHash = useAppStore((state) => state.settings.assetsHash);
  const isDemo = useDemo();
  const enableLobbyDeposit = useEnableLobbyDeposit();
  const enableDeposit = enableLobbyDeposit && !isDemo;
  
  const [activeTab, setActiveTab] = useState<TabType>(LANGUAGE);
  const [activeIcon, setActiveIcon] = useState<TabType>(LANGUAGE); 
  const modalContentRef = useRef<HTMLDivElement>(null);
  useSettleScrollOnRotate(modalContentRef);

  const { handleExit } = useAppStore();
  const { playExitAppSound, getExitSoundDuration } = useGameSounds();

  const handleExitWithSound = useCallback(() => {
      // Track with consistent naming: click_exit_settings
      clickCounter.incrementClick('click_exit_settings');

      // Play exit sound first
      playExitAppSound();

      // Get the duration of the exit sound for timing
      const exitSoundDuration = getExitSoundDuration();

      // Delay the actual exit to allow sound to play
      // Add a small buffer (100ms) to ensure sound completes
      setTimeout(() => {
          handleExit();
      }, exitSoundDuration + 100);
  }, [playExitAppSound, getExitSoundDuration, handleExit]);

  
  const handleLanguageChange = useCallback((newLanguageCode: string) => {
    updateSettings({ languageCode: newLanguageCode });
    // Notify the parent so it opens games in the language picked in the lobby
    // (the parent-handled launch path doesn't go through startLobbyGame).
    externalCommunication.sendLanguageChange(newLanguageCode);
  }, [updateSettings]);

  const resetScrollPosition = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  };
  
  const handleTabChange = useCallback((tab: TabType) => {
    if (tab === LOBBY) {
      setSelectedSection(LOBBY);
      setViewMode('multi');
      onClose();

      return;
    };

    if (tab === EXIT) {
      handleExitWithSound();
      setActiveIcon(tab);

      return;
    }

    if (tab === DEPOSIT) {
      // Deposit both fires the external action (the host opens its own deposit UI)
      // and navigates, so its tab can explain the balance-refresh delay. When the
      // flag is off or the session is a demo the icon stays inert: no action, and
      // nowhere to navigate, since that explanation is the tab's only content.
      if (!enableDeposit) return;

      externalCommunication.sendShowDeposit();
    }

    setActiveTab(tab);
    setActiveIcon(tab);
    resetScrollPosition();
  }, [onClose, handleExitWithSound, enableDeposit]);
  
  return (
    <Modal
      open={true}
      contentRef={modalContentRef}
      onClose={onClose}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
      title={t(TABS_META[activeTab].headerText ?? TABS_META[activeTab].text)}
      titleTestId={testIds.settings.modalTitle}
      closeButtonTestId={testIds.settings.modalCloseButton}
      footer={<SettingsModalFooter 
        activeIcon={activeIcon} 
        onSelectTab={handleTabChange}
        enableDeposit={enableDeposit}
      />}
    >
      {activeTab === LANGUAGE && <LanguageListComponent
        activeLanguageCode={languageCode}
        supportedLanguages={supportedLanguages}
        assetsHost={assetsHost}
        assetsHash={assetsHash}
        onSelectLanguage={handleLanguageChange}
      />}
      {activeTab === SETTINGS && <SettingTab />}
      {activeTab === HELP && <HelpTab/>}
      {activeTab === DEPOSIT && <DepositTab/>}
    </Modal>
  );
};

// Wrapper component - only subscribes to showSettingsModal
const SettingsModal: FC = () => {
  const showSettingsModal = useAppStore((state) => state.showSettingsModal);
  const setShowSettingsModal = useAppStore((state) => state.setShowSettingsModal);

  const handleClose = useCallback(() => {
    setShowSettingsModal(false);
  }, [setShowSettingsModal]);

  // Early return - don't render content when modal is closed
  if (!showSettingsModal) {
    return null;
  }

  return <SettingsModalContent onClose={handleClose} />;
};

export default SettingsModal;
